import { useEffect, useCallback, useLayoutEffect, useState } from 'react';
import { atom, useAtom } from 'jotai';
import { getPatchDataFromPartialDiff } from 'src/lib/utils/patch-utils';
import {
    getUserInfo,
    patchUserInfo,
    USER_INFO_QUERY_KEY,
} from 'src/services/userServices';
import { User } from 'src/services/types';
import { useLoyaltyAccountDetailsQuery } from 'src/services/easyRewardsServices';
import { SessionManager } from 'src/lib/api-client/sessionManager';
import { LOGIN_ROUTES } from 'src/routers/routes';
import { isListEnabled } from 'src/lib/api-client/lists';
import { useQuery, useQueryClient } from 'react-query';
import { removeStplSessionIdUniqueId } from 'src/lib/utils/app-utils';
import useErTooltips, { showUserTooltipsAtom } from './useErTooltips';
import useGuestMode from './useGuestMode';

/** common hook for fetching editable user info */
export default function useUserInfo(
    opts: {
        onSetUserInfoComplete?: (data: unknown, error: unknown) => void;
    } = {}
) {
    const queryClient = useQueryClient();

    const invalidateUserInfo = () => {
        queryClient.invalidateQueries({ queryKey: [USER_INFO_QUERY_KEY] });
    };
    const {
        data: userInfo,
        error: userInfoError,
        isFetching: isFetchingUserInfo,
        refetch: refetchUserInfo,
    } = getUserInfo();

    const {
        mutate: mutateUserInfo,
        error: patchUserInfoError,
        isLoading: isPatchingUserInfo,
        status: patchUserStatus,
    } = patchUserInfo({
        onSuccess: () => {
            refetchUserInfo();
        },
        onSettled: opts?.onSetUserInfoComplete,
    });
    const { isGuestModeOn } = useGuestMode();
    function setUserInfo(newUserInfo: Partial<User>) {
        // get diff between new and old info and begin sending patch data call
        mutateUserInfo(
            getPatchDataFromPartialDiff(userInfo || {}, newUserInfo)
        );
    }

    const { data: rewardsShippingListEnabled } = useQuery(
        ['rewardsShippingListEnabled'],
        () => isListEnabled('RewardShippers'),
        {
            refetchOnMount: true,
        }
    );

    useEffect(() => {
        if (
            userInfoError &&
            !isFetchingUserInfo &&
            SessionManager.isLoggedIn &&
            !isGuestModeOn
        ) {
            removeStplSessionIdUniqueId();
            window.location.replace(`somniapp${LOGIN_ROUTES.login}`);
            localStorage?.removeItem('login-details');
            SessionManager?.session?.killSession();
        }
    }, [userInfoError, isFetchingUserInfo]);

    return {
        userInfo,
        invalidateUserInfo,
        userInfoError,
        refetchUserInfo,
        isFetchingUserInfo,
        setUserInfo,
        patchUserStatus,
        patchUserInfoError,
        isPatchingUserInfo,
        isProcessing: isFetchingUserInfo || isPatchingUserInfo,
        error: userInfoError || patchUserInfoError,
        isRewardShipperEnable: rewardsShippingListEnabled?.isenabled,
    };
}
interface UserState {
    userType: 'new' | 'migrated' | 'non-migrated' | null;
    email: string | undefined | null;
}

const userStateAtom = atom<UserState>({
    userType: null,
    email: '',
});

export function useUserState(): {
    userState: UserState;
    setUserState: (newUserState: Partial<UserState>) => void;
    isUserStateLoading: boolean;
} {
    const [isUserStateLoading, setIsUserStateLoading] = useState(false);
    const [userState, setUserStateAction] = useAtom(userStateAtom);
    const [, setShowUserTooltips] = useAtom(showUserTooltipsAtom);
    const { resetTooltipStatus } = useErTooltips();
    const { userInfo, isFetchingUserInfo } = useUserInfo();
    const {
        data: loyaltyAccountDetails,
        isLoading: isLoyaltyAccountDetailsLoading,
    } = useLoyaltyAccountDetailsQuery({
        rewardsNumber: userInfo?.rewardsNumber,
        config: {
            enabled: !!userInfo?.rewardsNumber,
        },
    });

    const setUserState = useCallback(
        (newUserState: Partial<UserState>) => {
            setUserStateAction((currentUserState) => ({
                ...currentUserState,
                ...newUserState,
            }));
        },
        [setUserStateAction]
    );

    useLayoutEffect(() => {
        setIsUserStateLoading(true);
        if (!isFetchingUserInfo && !isLoyaltyAccountDetailsLoading) {
            if (
                userInfo?.rewardsNumber &&
                loyaltyAccountDetails?.accounts[0]
                    ?.termsandconditionaccepted === 'N'
            ) {
                setUserState({
                    userType: 'migrated',
                });
            } else if (userInfo && !userInfo?.rewardsNumber) {
                setUserState({
                    userType: 'non-migrated',
                });
            }
            setIsUserStateLoading(false);
        }
    }, [userInfo?.rewardsNumber, isLoyaltyAccountDetailsLoading]);

    // If it's a new user we have to show them the ER tooltips.
    useEffect(() => {
        if (userState.userType === 'new') {
            setShowUserTooltips(true);
            resetTooltipStatus();
        }
    }, [userState.userType]);

    return { userState, setUserState, isUserStateLoading };
}
